import { displayStageLabel } from '@/lib/stageLabels';

/**
 * Evidence-based pipeline stage status for Candidate Detail's PipelineTimeline.
 *
 * Do not use candidates.current_stage_code — it is stale post-Sprint 14 and
 * cannot represent out-of-order or multi-panel round completion.
 *
 * Status palette (product):
 * - complete → blue (done)
 * - pending  → amber (started / in progress, not done)
 * - upcoming → grey (never started)
 */

const PANEL_INTERVIEW_DONE_STATUSES = new Set(['completed', 'artifacts_ready', 'evaluated']);

function stageCodeKey(stage) {
  return String(stage?.stage_code || stage?.code || '').toLowerCase();
}

function linkStageCodes(code) {
  const upper = String(code || '').toUpperCase();
  if (upper === 'TECH_ASSESSMENT') return ['TECH_ASSESSMENT'];
  if (upper === 'VIDEOBOT') return ['VIDEOBOT'];
  if (upper === 'MCQ') return ['MCQ'];
  return [upper];
}

function offerFinalCallSaved(evidence) {
  // Backend flag, not the stored string: a Final Call saved for an interview that
  // was later replaced does not complete the current round.
  return evidence?.offer?.final_call_current === true;
}

function hasActiveLink(evidence, code) {
  const links = evidence.links;
  if (!Array.isArray(links) || !links.length) return false;
  const want = new Set(linkStageCodes(code));
  return links.some((l) => {
    if (!l || !want.has(String(l.stage_code || '').toUpperCase())) return false;
    const st = String(l.status || '').toLowerCase();
    // Treat anything not revoked/expired/consumed as "started" for stepper.
    if (st === 'revoked' || st === 'expired') return false;
    return true;
  });
}

/** True when hydrate shows AI overall and/or human panellist ratings. */
function panelHasClientScores(panel) {
  const ev = panel?.eval;
  if (!ev) return false;
  if (ev.eval_status === 'completed') return true;
  if (ev.human_overall_score != null) return true;
  const dims = ev.human_dimension_scores_json;
  if (Array.isArray(dims) && dims.some((d) => d && d.score != null)) return true;
  return false;
}

/**
 * Panel stage is blue only after the interview is finished *and* AI or human
 * scores exist. Recording-ready without scores stays amber.
 */
function isPanelRoundComplete(panel, stageId) {
  if (!panel || panel.pipeline_stage_id !== stageId) return false;
  if (panel.status === 'cancelled') return false;
  if (!PANEL_INTERVIEW_DONE_STATUSES.has(panel.status)) return false;
  return panelHasClientScores(panel);
}

function isPanelRoundPending(panel, stageId) {
  if (!panel || panel.pipeline_stage_id !== stageId) return false;
  if (panel.status === 'cancelled') return false;
  return !isPanelRoundComplete(panel, stageId);
}

/**
 * @param {{ id?: string, stage_code?: string, code?: string }} stage
 * @param {{
 *   hasResumeFile?: boolean,
 *   hasCompanyResume?: boolean,
 *   videobot?: { status?: string } | null,
 *   mcq?: object | null,
 *   tech_assessment?: object | null,
 *   panels?: Array<{
 *     pipeline_stage_id?: string | null,
 *     panel_kind?: string,
 *     status?: string,
 *     eval?: {
 *       eval_status?: string | null,
 *       human_overall_score?: number | null,
 *       human_dimension_scores_json?: Array<{ score?: number | null }> | null,
 *     } | null,
 *   }>,
 *   offer?: {
 *     has_active_interview?: boolean,
 *     completed?: boolean,
 *     final_call?: string | null,
 *     final_call_current?: boolean,
 *   } | null,
 *   links?: Array<{ stage_code?: string, status?: string }>,
 * }} evidence
 */
export function isStageComplete(stage, evidence = {}) {
  const code = stageCodeKey(stage);

  switch (code) {
    case 'resume':
      return Boolean(evidence.hasResumeFile || evidence.hasCompanyResume);
    case 'videobot':
      return evidence.videobot?.status === 'submitted';
    case 'mcq':
      // Candidate GET hydrate returns canonical scored/submitted only.
      return Boolean(evidence.mcq);
    case 'tech_assessment':
      return Boolean(evidence.tech_assessment);
    case 'panel':
    case 'code_panel': {
      const stageId = stage?.id;
      if (!stageId || !Array.isArray(evidence.panels)) return false;
      return evidence.panels.some((p) => isPanelRoundComplete(p, stageId));
    }
    case 'offer': {
      // Blue only after Save Final Call. Interview/feedback completeness is
      // `offer.completed`; it must not complete the stepper on its own.
      return evidence.offer?.completed === true && offerFinalCallSaved(evidence);
    }
    default:
      return false;
  }
}

/**
 * Started / in-progress but not complete (option a: any stage activity).
 */
export function isStagePending(stage, evidence = {}) {
  if (isStageComplete(stage, evidence)) return false;
  const code = stageCodeKey(stage);

  switch (code) {
    case 'resume':
      // Upload/company resume is complete-or-not; no distinct in-progress signal.
      return false;
    case 'videobot':
      if (evidence.videobot && evidence.videobot.status !== 'submitted') return true;
      return hasActiveLink(evidence, 'videobot');
    case 'mcq':
      return hasActiveLink(evidence, 'mcq');
    case 'tech_assessment':
      return hasActiveLink(evidence, 'tech_assessment');
    case 'panel':
    case 'code_panel': {
      const stageId = stage?.id;
      if (!stageId || !Array.isArray(evidence.panels)) return false;
      return evidence.panels.some((p) => isPanelRoundPending(p, stageId));
    }
    case 'offer': {
      return evidence.offer?.has_active_interview === true;
    }
    default:
      return false;
  }
}

/**
 * Build PipelineTimeline rows from pinned stage details (or fallback codes).
 *
 * @param {Array<{ id?: string, stage_code?: string, code?: string, label?: string, position?: number }>} stageDetails
 * @param {Parameters<typeof isStageComplete>[1]} evidence
 * @returns {Array<{ code: string, label: string, status: 'complete' | 'pending' | 'upcoming' }>}
 */
export function buildStageStatuses(stageDetails, evidence = {}) {
  const rows = Array.isArray(stageDetails) ? stageDetails : [];

  return rows.map((s) => {
    const code = String(s?.stage_code || s?.code || '').toUpperCase();
    const label = displayStageLabel(code, s?.label);

    let status = 'upcoming';
    if (isStageComplete(s, evidence)) {
      status = 'complete';
    } else if (isStagePending(s, evidence)) {
      status = 'pending';
    }

    return { code, label, status };
  });
}
